import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import { Plus, Minus } from 'lucide-react';

const SectionContainer = styled.section`
  padding: 120px 0;
  background: #3a3838;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 600px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(0, 151, 178, 0.4), transparent);
  }
`;

const Container = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 64px;
`;

const SectionTitle = styled(motion.h2)`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 16px;
  background: linear-gradient(135deg, #FAECEC 0%, #0097B2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled(motion.p)`
  color: rgba(250, 236, 236, 0.6);
  font-size: 1.05rem;
  line-height: 1.6;
`;

const ProductHuntWrapper = styled(motion.div)`
  display: flex;
  justify-content: center;
  margin-bottom: 60px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const FAQList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FAQItem = styled(motion.div)`
  border: 1px solid rgba(0, 151, 178, 0.18);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.03);
  overflow: hidden;
  transition: border-color 0.3s ease;

  &:hover {
    border-color: rgba(0, 151, 178, 0.38);
  }

  &[data-open='true'] {
    border-color: rgba(0, 151, 178, 0.45);
    background: rgba(0, 151, 178, 0.05);
  }
`;

const FAQQuestion = styled.button`
  width: 100%;
  background: none;
  border: none;
  padding: 22px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  cursor: pointer;
  text-align: left;
`;

const QuestionText = styled.span`
  font-size: 1.05rem;
  font-weight: 600;
  color: #FAECEC;
  line-height: 1.4;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 0.97rem;
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${props => props.open ? 'rgba(0, 151, 178, 0.25)' : 'rgba(255,255,255,0.07)'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s ease;
  color: ${props => props.open ? '#0097B2' : 'rgba(250,236,236,0.5)'};
`;

const AnswerWrapper = styled(motion.div)`
  overflow: hidden;
`;

const AnswerInner = styled.div`
  padding: 0 24px 22px;
  color: rgba(250, 236, 236, 0.72);
  font-size: 0.97rem;
  line-height: 1.7;
`;

const faqs = [
  {
    question: "What is f01i.ai?",
    answer: "f01i.ai is an AI-powered cryptocurrency trading platform that lets you create custom trading bots using LLMs (Claude, OpenAI, xAI, Gemini, DeepSeek). You can build bots with the visual Blocks editor, use the AI Bot Creator, or pick from ready-made template bots — all with zero trading commissions."
  },
  {
    question: "Which crypto exchanges does f01i.ai support?",
    answer: "f01i.ai currently supports Bybit, Binance, OKX, Coinbase, MEXC, Crypto.com, Hyperliquid and more — and we are constantly adding new exchanges and trading instruments. Simply connect your exchange API keys in Settings and your bots will trade automatically."
  },
  {
    question: "Are there any trading commissions?",
    answer: "No. f01i.ai charges zero trading commissions. You only pay for bot executions based on your subscription plan — we don't take a cut of your profits or trades."
  },
  {
    question: "How does the AI Bot Creator work?",
    answer: "Describe your trading strategy in plain English and the AI Bot Creator will generate a ready-to-deploy bot for you. You can select from LLMs like Claude, OpenAI GPT, Grok (xAI), Gemini, or DeepSeek. Use your own API key for unlimited requests at no extra platform cost."
  },
  {
    question: "What is f01i Blocks?",
    answer: "f01i Blocks is a visual workflow builder (Gamechanger+ feature) that lets you create complex AI trading strategies by connecting blocks — timers, data streams, AI models, exchange connectors, and more. No coding required."
  },
  {
    question: "Can I use my own AI API keys?",
    answer: "Yes. f01i.ai supports your own API keys for OpenAI, Anthropic, Google, xAI, and DeepSeek. When you use your own keys, platform credits are not deducted, giving you full cost control."
  },
  {
    question: "Is f01i.ai secure?",
    answer: "Absolutely. f01i.ai uses military-grade encryption for all API keys. We only use read-only and trading permissions — it is technically impossible for us to withdraw or transfer your funds. Your assets always stay in your exchange account."
  },
  {
    question: "What are the available plans?",
    answer: "f01i.ai offers a Free plan (3 Agent requests/day) and paid plans starting from $8/month that unlock more daily requests, additional trading bots, higher execution limits, and access to advanced features like Blocks. Visit the Plans page for full details."
  },
];

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const trustpilotRef = useRef(null);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  useEffect(() => {
    if (window.Trustpilot && trustpilotRef.current) {
      window.Trustpilot.loadFromElement(trustpilotRef.current, true);
    }
  }, []);

  return (
    <SectionContainer id="faq">
      <Container>
        <ProductHuntWrapper
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div
            ref={trustpilotRef}
            className="trustpilot-widget"
            data-locale="en-US"
            data-template-id="56278e9abfbbba0bdcd568bc"
            data-businessunit-id="69a02d6f32f3fa7b8fe18e58"
            data-style-height="52px"
            data-style-width="100%"
            data-token="4cfd361e-8ae3-4823-b852-19752b2e8d1b"
          >
            <a href="https://www.trustpilot.com/review/f01i.ai" target="_blank" rel="noopener noreferrer">Trustpilot</a>
          </div>
        </ProductHuntWrapper>

        <SectionHeader>
          <SectionTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            Frequently Asked Questions
          </SectionTitle>
          <SectionSubtitle
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            viewport={{ once: true }}
          >
            Everything you need to know about f01i.ai
          </SectionSubtitle>
        </SectionHeader>

        <FAQList>
          {faqs.map((item, i) => (
            <FAQItem
              key={i}
              data-open={openIndex === i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: i * 0.06 }}
              viewport={{ once: true }}
              data-testid={`faq-item-${i}`}
            >
              <FAQQuestion
                onClick={() => toggle(i)}
                aria-expanded={openIndex === i}
                data-testid={`faq-question-${i}`}
              >
                <QuestionText>{item.question}</QuestionText>
                <IconWrapper open={openIndex === i}>
                  {openIndex === i ? <Minus size={15} /> : <Plus size={15} />}
                </IconWrapper>
              </FAQQuestion>

              <AnimatePresence initial={false}>
                {openIndex === i && (
                  <AnswerWrapper
                    key="answer"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <AnswerInner data-testid={`faq-answer-${i}`}>
                      {item.answer}
                    </AnswerInner>
                  </AnswerWrapper>
                )}
              </AnimatePresence>
            </FAQItem>
          ))}
        </FAQList>
      </Container>
    </SectionContainer>
  );
};

export default FAQSection;
